import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import React from "react";
import type { Output } from "../../../../../api/call";
import t from "../../../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { color, fonts } from "@ui";
import Checkbox from "@ui/Checkbox";
import Ellipsis from "@ui/Ellipsis";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import ModalForm from "@ui/Modal/Form";
import Text from "@ui/Text";

const Content = styled(ModalForm)`
  overflow-y: hidden;
`;

const Container = styled.div`
  font: ${fonts.body1};
  height: 100%;
  flex: 1;
  min-height: 0;
`;

const FormContainer = styled.div`
  margin: 8px auto;
  min-width: 300px;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const RowsContainer = styled.div`
  max-height: min(60vh, 620px);
  overflow-y: auto;
`;

const Description = styled(Text)`
  margin: 0 0 16px;
`;

const StickySection = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
`;

const SearchInput = styled(SearchInputV2)`
  margin-bottom: 12px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  color: ${color.carbon800};
  font: ${fonts.body2SemiBold};
  background-color: ${color.titanium100};
  width: 100%;
  height: 50px;
  flex-shrink: 0;
`;

const Name = styled.span`
  display: inline-block;
  margin-left: 10px;
`;

const Row = styled.div`
  display: flex;
  padding-right: 10px;
  height: 44px;
  border-bottom: solid 1px ${color.titanium100};
`;

const EmptyRowText = styled(Text)`
  margin-left: 10px;
`;

const customCheckbox = css`
  margin-left: 10px;
  transform: scale(0.8);
`;

export type Sensor = Output<"GET", "/projects/:projectId(uuid)/sensors">["body"][number];

interface P {
  availableSensors: NonEmptyArray<Sensor>;
  onRequestClose: () => void;
  createQuantities: (selection: Sensor[]) => Promise<void>;
  sourceId: string;
}
const CreateSensorQuantityModal = ({
  availableSensors,
  sourceId,
  onRequestClose,
  createQuantities,
}: P) => {
  const [selection, setSelection] = React.useState<Sensor[]>([]);
  const [search, setSearch] = React.useState("");

  const normalizedSearch = React.useMemo(() => search.trim().toLocaleLowerCase(), [search]);

  const visibleSensors = React.useMemo(() => {
    if (normalizedSearch.length < 3) return availableSensors;
    return availableSensors.filter(sensor =>
      sensor.tag.toLocaleLowerCase().includes(normalizedSearch),
    );
  }, [availableSensors, normalizedSearch]);

  const visibleSensorIds = React.useMemo(
    () => new Set(visibleSensors.map(sensor => sensor.id)),
    [visibleSensors],
  );

  const selectedVisibleSensorsCount = React.useMemo(
    () => selection.filter(sensor => visibleSensorIds.has(sensor.id)).length,
    [selection, visibleSensorIds],
  );

  const toggleAll = React.useCallback(() => {
    if (selectedVisibleSensorsCount === visibleSensors.length) {
      setSelection(selection => selection.filter(sensor => !visibleSensorIds.has(sensor.id)));
      return;
    }

    setSelection(selection => [
      ...selection.filter(sensor => !visibleSensorIds.has(sensor.id)), // hidden selection remains in place
      ...visibleSensors,
    ]);
  }, [selectedVisibleSensorsCount, visibleSensorIds, visibleSensors]);

  const toggleSensor = React.useCallback(
    (sensor: Sensor) => () => {
      if (selection.find(s => s.id === sensor.id)) {
        setSelection(selection => [...selection.filter(s => s.id !== sensor.id)]);
      } else {
        setSelection(selection => [...selection, sensor]);
      }
    },
    [selection],
  );

  const handleSubmit = React.useCallback(async () => {
    await createQuantities(selection);
    return true;
  }, [selection, createQuantities]);

  return (
    <Content
      onRequestClose={onRequestClose}
      onSubmit={selection.length > 0 ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.add")}
    >
      <Container>
        <FormContainer>
          <RowsContainer>
            <Description>
              {t("project.quantities.newQuantities.modal.description", { sourceId })}
            </Description>
            <StickySection>
              <SearchInput
                placeholder={t("commons.placeholder.search")}
                value={search}
                onChange={event => setSearch(event.currentTarget.value)}
              />
              <Header>
                <Checkbox
                  indicatorClass={customCheckbox}
                  checked={
                    visibleSensors.length > 0 &&
                    selectedVisibleSensorsCount === visibleSensors.length
                  }
                  onChange={toggleAll}
                  partial={
                    selectedVisibleSensorsCount > 0 &&
                    selectedVisibleSensorsCount < visibleSensors.length
                  }
                >
                  <Name>{t("project.quantities.newQuantities.modal.table.column.name")}</Name>
                </Checkbox>
              </Header>
            </StickySection>
            {visibleSensors.length === 0 ? (
              <Row>
                <EmptyRowText>{t("common.search.no_result")}</EmptyRowText>
              </Row>
            ) : (
              visibleSensors.map(sensor => (
                <Row key={sensor.id}>
                  <Checkbox
                    indicatorClass={customCheckbox}
                    checked={selection.find(s => s.id === sensor.id) !== undefined}
                    onChange={toggleSensor(sensor)}
                  >
                    <Ellipsis title={sensor.tag}>
                      <Name>{sensor.tag}</Name>
                    </Ellipsis>
                  </Checkbox>
                </Row>
              ))
            )}
          </RowsContainer>
        </FormContainer>
      </Container>
    </Content>
  );
};

export default React.memo(CreateSensorQuantityModal);
